import { useCallback, useState } from 'react';
import { WithdrawalReportStatusDataType } from 'commons/services/withdrawalReportService';
import { ReportStatus } from 'commons/types';
import { nextReportAttemptId } from './reportAttemptId';
import { useCreateWithdrawalReportMutation } from './useCreateWithdrawalReportMutation';
import { useGetWithdrawalReportStatusPolling } from './useGetWithdrawalReportStatusPolling';

interface UseWithdrawalReportGenerationProps {
  onError?: () => void;
}

export function useWithdrawalReportGeneration({
  onError,
}: UseWithdrawalReportGenerationProps = {}) {
  const [requestId, setRequestId] = useState<number | undefined>();
  const [report, setReport] = useState<
    WithdrawalReportStatusDataType | undefined
  >();
  const [attemptId, setAttemptId] = useState<number | undefined>(undefined);

  const createReportMutation = useCreateWithdrawalReportMutation({
    onSuccess: (requestId) => {
      setRequestId(requestId);
    },
    onError,
  });

  const { isPolling } = useGetWithdrawalReportStatusPolling({
    requestId,
    attemptId,
    onFinished: ({ status, temporaryDownloadUrl }) => {
      setRequestId(undefined);
      setReport({ status, temporaryDownloadUrl });
      if (status === ReportStatus.Failed) {
        onError?.();
      }
    },
    onError: () => {
      setRequestId(undefined);
      setReport(undefined);
      onError?.();
    },
  });

  const { mutate: createReport } = createReportMutation;

  const startReportGeneration = useCallback(
    (withdrawalId: number) => {
      setReport(undefined);
      setAttemptId(nextReportAttemptId());
      createReport(withdrawalId);
    },
    [createReport],
  );

  const isLoading = isPolling || createReportMutation.isPending;

  return {
    startReportGeneration,
    isLoading,
    report,
  };
}
